import React, { useState } from 'react';
import '../../ClientCard/ContentPages/Sales/Sales.scss';
import BemClass from '@upsales/components/Utils/bemClass';
import SimpleListView from '../../ClientCard/SimpleListView';
import SalesCardFilterHeader from '../../ClientCard/SimpleListView/SalesCardFilterHeader';
import Order from 'App/resources/Model/Order';
import RequestBuilder from 'Resources/RequestBuilder';
import OrderResource from 'App/resources/Order';
import OrderAttributes from 'App/babel/attributes/Order';
import ARRChangesAttributes from 'App/babel/attributes/ARRChangesAttributes';
import ComparisonTypes from 'Resources/ComparisonTypes';
import moment from 'moment';
import Agreement from 'App/resources/Model/Agreement';
import { Headline, Button, DropDownMenu, Text, Icon, ClickableItem, Row } from '@upsales/components';
import AgreementAttributes from 'App/babel/attributes/AgreementAttributes';
import T from 'Components/Helpers/translate';
import { useSelector, useSoftDeployAccess } from 'App/components/hooks';
import { useSalesCardOrderFilters } from 'App/components/hooks/filters';
import { RootState } from 'Store/index';
import ARRChangeResource from 'App/babel/resources/ARRChanges';
import { ARRChange } from 'App/resources/Model/ARRChange';
import ClientSales from 'App/components/ClientSales';
import openModal from 'App/services/Modal/Modal';
import Client from 'App/resources/Model/Client';
import IconName from '@upsales/components/Icon/IconName';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { getFeatureAvailableFromState, getSoftDeployAccessFromState } from 'App/components/hooks/featureHelper';
import { AppState } from 'Store/reducers/AppReducer';
import { openEditOrder } from 'App/components/EditOrder';
import { CardEntity } from './types';
import AppService from 'App/legacy/AppService';

const COLUMNS = ['date', 'description', 'contact', 'value', 'stage', 'user', 'delete'];
const COLUMNS_AGREEMENT = ['startDate', 'nextOrderDate', 'description', 'contact', 'yearlyValue', 'user', 'delete'];

const createActions = [
	{
		title: 'default.order',
		type: 'order',
		show: ({ accountSelf }) => {
			return getFeatureAvailableFromState(accountSelf, Feature.ORDERS);
		},
		icon: 'dollar',
		clickAction: client => {
			openEditOrder({ type: 'order', clientId: client.id });
		}
	},
	{
		title: 'default.agreement',
		type: 'agreement',
		show: ({ metadata, accountSelf, accessRights }) => {
			return (
				!!metadata?.params.AgreementEnabled &&
				getFeatureAvailableFromState(accountSelf, Feature.RECURRING_ORDER) &&
				accessRights.Agreement
			);
		},
		icon: 'recurring-order',
		clickAction: (client, { accountSelf }) => {
			if (getSoftDeployAccessFromState(accountSelf, 'SUBSCRIPTION_MODAL')) {
				openModal('CreateSubscription', { createdFrom: 'accountSalesAddButton', dontWait: true, client });
			} else {
				Tools.$upModal.open('editAgreement', { clientId: client.id });
			}
		}
	}
] satisfies {
	icon: IconName;
	type: string;
	title: string;
	show?: (appState: AppState) => boolean;
	clickAction: (client: Client, appState: AppState) => void;
}[];

const getData = async (
	rb: RequestBuilder,
	cardEntity: CardEntity
): Promise<{
	data: Order[];
	metadata: {
		total: number;
	};
}> => {
	const excludedStages = await Tools.OrderStage.customer(AppService.getCustomerId()).find({ exclude: 1 });
	rb.addFilter(OrderAttributes.probability, ComparisonTypes.Equals, 100);
	rb.addFilter(
		OrderAttributes.stage,
		ComparisonTypes.NotEquals,
		excludedStages.data.map(stage => stage.id)
	);

	if (cardEntity.type === 'client') {
		const clientOr = rb.orBuilder();
		clientOr.next();
		clientOr.addFilter(OrderAttributes.account, ComparisonTypes.Equals, cardEntity.id);
		clientOr.next();
		clientOr.addFilter(OrderAttributes.clientConnection, ComparisonTypes.Equals, cardEntity.id);
		clientOr.done();
	} else {
		rb.addFilter({ field: 'contact.id' }, ComparisonTypes.Equals, cardEntity.id);
	}

	return OrderResource.find(rb.build());
};

const getAgreementData = (
	rb: RequestBuilder,
	{ cardEntity, inactiveAgreements }: { cardEntity: CardEntity; inactiveAgreements: boolean }
) => {
	if (cardEntity.type === 'client') {
		rb.addFilter(AgreementAttributes.client, ComparisonTypes.Equals, cardEntity.id);
	} else {
		rb.addFilter(AgreementAttributes.contact, ComparisonTypes.Equals, cardEntity.id);
	}

	if (inactiveAgreements) {
		rb.addFilter(AgreementAttributes.endDate, ComparisonTypes.LessThanEquals, moment().format('YYYY-MM-DD'));
	} else {
		const orBuilder = rb.orBuilder();
		orBuilder.next();
		orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.GreaterThan, moment().format('YYYY-MM-DD'));
		orBuilder.next();
		orBuilder.addFilter(AgreementAttributes.endDate, ComparisonTypes.Equals, null);
		orBuilder.done();
	}
	rb.addSort(AgreementAttributes.startDate);

	const rbBuild = rb.build();
	return Tools.Agreement.customer(AppService.getCustomerId()).find(rbBuild);
};

const generateSubHeader = (item: Order, sorting: string) => {
	switch (sorting) {
		case 'date':
			return moment(item.date).year().toString();
		case 'user.name':
			return item.user.name;
		case 'stage.id':
			return item.stage.name;
	}
	return '';
};

const generateSubHeaderAgreement = (item: Agreement, sorting: string) => {
	switch (sorting) {
		case 'metadata.agreementStartdate':
			return moment(item.metadata.agreementStartdate).isAfter()
				? T('agreement.futureAgreements')
				: T('defaultView.activeAgreements');
		case 'user.name':
			return item.user.name;
	}
	return '';
};

const generateSubHeaderARRChanges = (item: ARRChange, sorting: string) => {
	switch (sorting) {
		case 'date':
			return moment(item.date).year().toString();
		case 'type':
			return T(`arrchange.type.${item.type}`);
	}
	return '';
};

const onRowClick =
	({ id }: Order) =>
	() => {
		openEditOrder({ id, type: 'order' });
	};

const onAgreementRowClick =
	({ id }: Agreement) =>
	() => {
		Tools.$upModal.open('editAgreement', { id });
	};

const SalesAddButton = ({ cardEntity }: { cardEntity: CardEntity }) => {
	const classes = new BemClass('SalesAddButton');
	const appData = useSelector(({ App }) => App);
	const client = cardEntity.type === 'client' ? cardEntity.data : (cardEntity.data.client as Client);

	return (
		<DropDownMenu
			className={classes.b()}
			align="right"
			renderTrigger={(expanded, setExpanded) => (
				<Button size="sm" onClick={setExpanded}>
					<Text color="white">
						<Icon name="plus" space="prm" />
						{T('default.add')}
					</Text>
				</Button>
			)}
			noMinWidth
		>
			{close => (
				<>
					{createActions.map(item =>
						item.show === undefined || item.show(appData) ? (
							<ClickableItem
								key={item.type}
								onClick={() => {
									close();
									item.clickAction(client, appData);
								}}
								title={T(item.title)}
								icon={item.icon}
								block
							/>
						) : null
					)}
				</>
			)}
		</DropDownMenu>
	);
};

const SalesAgreementList = ({
	classes,
	inactiveAgreements,
	cardEntity
}: {
	classes: BemClass;
	inactiveAgreements: boolean;
	cardEntity: CardEntity;
}) => (
	<SimpleListView<Agreement>
		classes={classes}
		getData={rb => getAgreementData(rb, { cardEntity, inactiveAgreements })}
		columns={COLUMNS_AGREEMENT}
		subHeaderFn={inactiveAgreements ? undefined : generateSubHeaderAgreement}
		attributes={{
			...AgreementAttributes,
			description: {
				...AgreementAttributes.description,
				sortable: false
			},
			contact: {
				...AgreementAttributes.contact,
				placeholder: 'default.noContact'
			},
			nextOrderDate: {
				...AgreementAttributes.nextOrderDate,
				placeholder: 'agreement.noNextOrderDate'
			}
		}}
		rowStyles={{
			inactive: () => inactiveAgreements
		}}
		isDeletable={item => {
			return !!item.userRemovable;
		}}
		deleteFn={agreement => {
			return Tools.Agreement.customer(AppService.getCustomerId())['delete'](agreement);
		}}
		entityName={'default.agreement'}
		onRowClick={onAgreementRowClick}
		renderHeader={() => (
			<Row className="SimpleListView__header">
				<Headline size="sm">
					{inactiveAgreements ? T('default.agreementsInactive') : T('agreement.agreements')}
				</Headline>
				<div className="FlexPadder" />
				{!inactiveAgreements ? <SalesAddButton cardEntity={cardEntity} /> : null}
			</Row>
		)}
		broadcastType="order"
		initialSorting={[{ attribute: 'metadata.agreementStartdate', ascending: false }]}
		tableLimitOptions={[25]}
	/>
);

type SharedSalesProps = {
	cardEntity: CardEntity;
	// Only used for the client variant (ClientSales widgets) — the client card page pre-fetches it
	accountCardData?: any;
};

const SharedSales = ({ cardEntity, accountCardData }: SharedSalesProps) => {
	const classes = new BemClass('ClientCardContent__Sales');
	const salesData = accountCardData?.['sales']?.data;

	const [showInactiveAgreements, setShowInactiveAgreements] = useState(false);
	const hasSalesCardFilters = useSoftDeployAccess('SALES_CARD_FILTERS');
	const salesCardFilters = useSalesCardOrderFilters();

	const { SalesModel: salesModel, SalesModelOption: salesModelOption } = useSelector(
		(state: RootState) => state.App.metadata!.params
	);
	const [graphModel, setGraphModel] = useState<'recurring' | 'cashflow'>(
		salesModel === 'rr' ? 'recurring' : 'cashflow'
	);

	const shouldShowAgreements = (salesModel === 'rr' && graphModel === 'recurring') || salesModel !== 'rr';
	const shouldShowOrders = (salesModel === 'rr' && graphModel === 'cashflow') || salesModel !== 'rr';

	const cmRatio = (salesData?.CMPast12Months ?? 0) / salesData?.salesPast12Months || 0;

	return (
		<div className={classes.b()}>
			{cardEntity.type === 'client' ? (
				<ClientSales
					quotas={(cardEntity.data as Client).clientQuotas}
					clientId={cardEntity.id}
					setGraphType={t => setGraphModel(t)}
					arrChangeLast12Months={(cardEntity.data as Client).growth?.arrLast12Months ?? 0}
					mrrChangeLast12Months={(cardEntity.data as Client).growth?.mrrLast12Months ?? 0}
					currentARR={(cardEntity.data as Client).growth?.arr ?? 0}
					contributionMarginTotal={salesData?.CMPast12Months ?? 0}
					contributionMarginRatio={cmRatio}
					orderTotal={salesData?.salesPast12Months ?? 0}
					hideTarget
				/>
			) : null}

			{shouldShowAgreements ? (
				<>
					<SalesAgreementList inactiveAgreements={false} cardEntity={cardEntity} classes={classes} />
					{showInactiveAgreements ? (
						<SalesAgreementList inactiveAgreements cardEntity={cardEntity} classes={classes} />
					) : null}
					<Button
						className={classes.elem('showInactiveAgreementToggler').b()}
						type="link"
						onClick={() => setShowInactiveAgreements(!showInactiveAgreements)}
					>{`${T(showInactiveAgreements ? 'default.hide' : 'default.show')} ${T(
						'default.agreementsInactive'
					)}`}</Button>

					<SimpleListView<ARRChange>
						classes={classes}
						getData={rb => {
							if (cardEntity.type === 'client') {
								rb.addFilter(AgreementAttributes.client, ComparisonTypes.Equals, cardEntity.id);
							} else {
								rb.addFilter(AgreementAttributes.contact, ComparisonTypes.Equals, cardEntity.id);
							}
							return ARRChangeResource.find(rb.build());
						}}
						columns={[
							'date',
							'type',
							salesModelOption === 'arr' ? 'annualValueInMasterCurrency' : 'monthlyValueInMasterCurrency',
							'users'
						]}
						subHeaderFn={generateSubHeaderARRChanges}
						attributes={ARRChangesAttributes}
						renderHeader={() => (
							<Headline size="sm">
								{T(salesModelOption === 'arr' ? 'default.arr' : 'default.mrr')}{' '}
								{T('default.changes').toLowerCase()}
							</Headline>
						)}
						onRowClick={change => () => {
							openModal('ARRChangeDrawerV2', {
								clientId: cardEntity.type === 'contact' ? cardEntity.data.client.id : cardEntity.id,
								date: change.date,
								type: 'arrchange.type.' + change.type
							});
						}}
						broadcastType="order"
						initialSorting={[{ attribute: 'date', ascending: false }]}
						tableLimitOptions={[25]}
					/>
				</>
			) : null}

			{shouldShowOrders ? (
				<SimpleListView<Order>
					classes={classes}
					getData={rb => getData(rb, cardEntity)}
					columns={COLUMNS}
					subHeaderFn={generateSubHeader}
					attributes={{
						...OrderAttributes,
						description: {
							...OrderAttributes.description,
							sortable: false
						},
						contact: {
							...OrderAttributes.contact,
							placeholder: 'default.noContact'
						}
					}}
					rowStyles={{
						inactive: item => item.probability === 0
					}}
					deleteFn={order => {
						return OrderResource.delete(order.id);
					}}
					isDeletable={order => {
						return !!order.userRemovable;
					}}
					entityName="default.order"
					onRowClick={onRowClick}
					filterConfigs={hasSalesCardFilters ? salesCardFilters : undefined}
					quickSearchFilter={hasSalesCardFilters ? 'ListSearch' : undefined}
					renderHeader={
						hasSalesCardFilters
							? provided => (
									<SalesCardFilterHeader
										provided={provided}
										title={<Headline size="sm">{T('default.orders')}</Headline>}
										extra={<SalesAddButton cardEntity={cardEntity} />}
									/>
							  )
							: () => (
									<Row className="SimpleListView__header">
										<Headline size="sm">{T('default.orders')}</Headline>
										<div className="FlexPadder" />
										<SalesAddButton cardEntity={cardEntity} />
									</Row>
							  )
					}
					broadcastType="order"
					initialSorting={[{ attribute: 'date', ascending: false }]}
					tableLimitOptions={[25]}
				/>
			) : null}
		</div>
	);
};

export default SharedSales;
